import { faCrown } from '@fortawesome/free-solid-svg-icons/faCrown';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraEmpty } from '../../components/AtiraEmpty';
import { Button } from '../../components/Button';
import { CopyButton } from '../../components/CopyButton';
import { Flex } from '../../components/Flex';
import { Input } from '../../components/Input';
import { PaidFeatureNotice } from '../../components/PaidFeatureNotice';
import { Text } from '../../components/Text';
import { Modal } from '../../components/modal/Modal';
import { urlTestIDs } from '../../constants/test/url.testIds';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { AtiraLinkPlanType } from '../../model/plan/types/AtiraLinkPlanType.enum';
import { Lengths } from '../../model/shared/enum/Lengths.enum';
import { ShortenURLDto } from '../../model/url/dto/ShortenURLDto';
import { usePlansContext } from '../../plans-context';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { urlSliceSelectors } from '../../redux/url/url.selector';
import { shortenUrlActions } from '../../redux/url/url.slice';
import { userSliceSelectors } from '../../redux/user/user.selector';
import { Breakpoints } from '../../theme/Breakpoints';
import { FontSize } from '../../theme/FontSize';
import { Rounded } from '../../theme/Rounded';
import { Spacing } from '../../theme/Spacing';
import { AtiraToast } from '../../utils/AtiraToast';
import { onSetTypedCRUDErrors } from '../../utils/onSetTypedCRUDErrors';

const InputButtonWrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: ${Spacing.m};
`;

const ResponseWrapper = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.s};

  @media (min-width: ${Breakpoints.MOBILE}) {
    width: 60%;
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    width: 40%;
  }
`;

const StyledCopyButton = styled(CopyButton)`
  padding: 0;
`;

const StyledText = styled(Text)`
  word-break: break-all;
  direction: ltr;
  line-height: normal;
  font-size: ${FontSize.s};

  html[dir='rtl'] & {
    text-align: right;
  }
`;

const Wrapper = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.xxl};
  width: 100%;

  @media (min-width: ${Breakpoints.MOBILE}) {
    flex-direction: row;
  }
`;

const NewURLContainer = styled(Flex)`
  flex-direction: column;
  border-radius: ${Rounded.lg};
  width: 100%;
  position: relative;
  z-index: 1;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-color: ${({ theme }) => theme.main};
    opacity: 0.1;
    z-index: -1;
  }
`;

const NewURLTitle = styled(Text)`
  font-weight: bold;
  text-align: center;
  padding: ${Spacing.s} ${Spacing.m};
  background-color: ${({ theme }) => theme.main};
  color: ${({ theme }) => theme.onMain};
`;

const NewURLWrapper = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.m};
`;

const StyledButton = styled(Button)`
  height: 2.3rem;
  width: 8rem;

  &:hover {
    background-color: ${({ theme }) => theme.mainHover};
  }
`;

type URLShortnerModalProps = {
  open: boolean;
  onClose: VoidFunction;
};

export const URLShortnerModal: React.FC<URLShortnerModalProps> = ({
  open = false,
  onClose,
}) => {
  const { t } = useTranslation();

  const loggedInUser = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const userURLsLength = useAppSelector((s) =>
    urlSliceSelectors.selectShortURLs(s, loggedInUser._id),
  ).length;

  const { setError, getValues, control, reset, handleSubmit } =
    useForm<ShortenURLDto>({
      defaultValues: { originalURL: '', userId: loggedInUser._id! },
    });

  const [currentURL, setCurrentURL] = useState<
    | {
        shortURL: string;
        label?: string;
      }
    | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [reachedLimit, setReachedLimit] = useState(
    Number(loggedInUser.plan?.perks?.urlsCount) <= userURLsLength,
  );

  const { setPlansModalVisible } = usePlansContext();
  const { width } = useWindowDimensions();

  const dispatch = useAppDispatch();

  const userPlan =
    loggedInUser?.plan?.freeTrial?.freeTrialPlan ||
    loggedInUser?.plan?.unpaidPlan?.unpaidPlanType ||
    loggedInUser.plan?.type;

  const isOnMobile = width < Number(Breakpoints.MOBILE.replace('px', ''));

  const onReset = () => {
    reset({
      customIdentifier: '',
      originalURL: '',
      label: '',
      userId: loggedInUser._id!,
    });
  };

  const onLocalClose = () => {
    setCurrentURL(undefined);
    onReset();
    onClose();
  };

  const onShortenURL = async () => {
    try {
      const url = getValues('originalURL').trim();
      const customIdentifier = getValues('customIdentifier')?.trim();
      const label = getValues('label')?.trim();

      setLoading(true);

      if (!url) {
        AtiraToast.error(t('shorten_url.create.error.enter_url'));
        return;
      }

      const response = await dispatch(
        shortenUrlActions.getShortURL({
          userId: loggedInUser._id!,
          originalURL: url,
          customIdentifier,
          label,
        }),
      ).unwrap();

      await dispatch(
        shortenUrlActions.getUserShortURLs({ userId: loggedInUser._id }),
      ).unwrap();

      setCurrentURL({ shortURL: response.url.shortURL!, label });

      AtiraToast.success(t('shorten_url.create.success'));
      onReset();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const shouldUpgrade = () => {
      const URLCountLimit = Number(loggedInUser.plan?.perks?.urlsCount);

      if (URLCountLimit <= userURLsLength) {
        setReachedLimit(true);
        return;
      }

      setReachedLimit(false);
    };

    shouldUpgrade();
  }, [userURLsLength, loggedInUser.plan?.perks?.urlsCount]);

  return (
    <Modal
      title={t('shortenURL.modal.title')}
      open={open}
      onClose={onLocalClose}
      width={'60rem'}
      maskClosable={false}
      centered
    >
      <Wrapper>
        <Flex flexDirection="column" gap="m" flexGrow={1}>
          <Text>{t('shorten_url.title')}</Text>
          <Text color="fgSecondary" fontSize="s">
            {t('shorten_url.description')}
          </Text>

          <InputButtonWrapper>
            <Controller
              control={control}
              name="originalURL"
              rules={{
                required: {
                  value: true,
                  message: t('common.this_field_required'),
                },
              }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Input
                  title={t('shorten_url.input.label')}
                  placeholder="https://example.com"
                  value={value}
                  onChange={onChange}
                  valid={!error}
                  errorMessage={error?.message}
                  dataTestId={urlTestIDs.shorten.url}
                  type="url"
                  height="2.5rem"
                  required
                  info={t('shortenURL.input.info')}
                />
              )}
            />

            <Controller
              control={control}
              name="label"
              rules={{
                maxLength: {
                  value: Lengths.DESCRIPTION,
                  message: t('error.length.less', {
                    name: t('common.label'),
                    length: Lengths.DESCRIPTION,
                  }),
                },
              }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Input
                  title={t('common.label')}
                  placeholder={t('shortenURL.label.placeholder')}
                  value={value}
                  onChange={onChange}
                  valid={!error}
                  errorMessage={error?.message}
                  dataTestId={urlTestIDs.shorten.label}
                  height="2.5rem"
                  info={t('shortenURL.label.info')}
                  maxLength={Lengths.DESCRIPTION}
                />
              )}
            />

            <Flex flexDirection="column" gap="s" marginBottom="l">
              <Controller
                control={control}
                name="customIdentifier"
                rules={{
                  pattern: {
                    value: /^[a-zA-Z0-9]{6}$/,
                    message: t('shortenURL.customIdentifier.pattern.error'),
                  },
                }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    title={t('shortenURL.customIdentifier')}
                    placeholder={'abc123'}
                    info={t('shortenURL.customIdentifier.info')}
                    value={value}
                    onChange={onChange}
                    valid={!error}
                    errorMessage={error?.message}
                    dataTestId={urlTestIDs.shorten.customIdentifier}
                    type="text"
                    height="2.5rem"
                    disabled={userPlan !== AtiraLinkPlanType.PLAN_2}
                    maxLength={6}
                  />
                )}
              />

              {userPlan !== AtiraLinkPlanType.PLAN_2 ? (
                <PaidFeatureNotice plan={AtiraLinkPlanType.PLAN_2} />
              ) : null}
            </Flex>

            <StyledButton
              onClick={
                !reachedLimit
                  ? handleSubmit(onShortenURL)
                  : () => setPlansModalVisible(true)
              }
              loading={loading}
              icon={reachedLimit ? faCrown : undefined}
              dataTestId={urlTestIDs.shorten.button}
            >
              {!reachedLimit
                ? t('shorten_url.button_text')
                : t('common.upgrade')}
            </StyledButton>
          </InputButtonWrapper>
        </Flex>

        <ResponseWrapper>
          {currentURL ? (
            <NewURLContainer>
              <NewURLTitle>{t('shorten_url.response.new_url')}</NewURLTitle>

              <Flex flexDirection="column" gap="s" padding="m">
                {currentURL?.label ? (
                  <Text align="center" fontWeight={'bold'} cutText>
                    {currentURL.label}
                  </Text>
                ) : null}

                <NewURLWrapper>
                  <StyledText>{currentURL.shortURL}</StyledText>
                  <StyledCopyButton copy={currentURL.shortURL} iconSize="lg" />
                </NewURLWrapper>
              </Flex>
            </NewURLContainer>
          ) : (
            <AtiraEmpty
              iconHeight="5rem"
              fontSize="m"
              icon={faInfoCircle}
              description={t(
                `shortenURL.preview.initial${isOnMobile ? '.mobile' : ''}`,
              )}
            />
          )}
        </ResponseWrapper>
      </Wrapper>
    </Modal>
  );
};
